import ApiService from './apiService';
import { useNotificationsStore } from '@/store';

class NoticationsService {
    formatCurrency(amount) {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'XOF',
            minimumFractionDigits: 0
        }).format(Number(amount || 0))
    }

    formatPaymentType(paymentType) {
        const labels = {
            cash: 'Espèces',
            card: 'Carte bancaire',
            wave: 'Wave',
            orange_money: 'Orange Money',
            mobile_money: 'Mobile Money',
            bank_transfer: 'Virement bancaire',
            check: 'Chèque',
            wallet: 'Portefeuille'
        }

        return labels[paymentType] || paymentType || 'Inconnu'
    }

    buildPaymentReceivedMessage({ amount, customerName, customerId, paymentType, saleRef, invoiceRef }) {
        const parts = [
            `Encaissement client: ${this.formatCurrency(amount)} recu`,
            `via ${this.formatPaymentType(paymentType)}`
        ]

        if (customerName) {
            parts.push(`de ${customerName}`)
        } else if (customerId) {
            parts.push(`du client #${customerId}`)
        }

        if (saleRef) {
            parts.push(`(${saleRef})`)
        } else if (invoiceRef) {
            parts.push(`(Facture ${invoiceRef})`)
        }

        return parts.join(' ')
    }

    buildWalletCreditMessage({ amount, customerName, customerId, sourceRef }) {
        const customerLabel = customerName || (customerId ? `Client #${customerId}` : 'client')
        const sourceSuffix = sourceRef ? ` (${sourceRef})` : ''
        return `Credit portefeuille: ${this.formatCurrency(amount)} ajoutes pour ${customerLabel}${sourceSuffix}`
    }

    buildCustomerInvoiceCreatedMessage({ invoiceNumber, customerName, customerId, salesCount }) {
        const invoiceRef = invoiceNumber || 'N/A'
        const customerLabel = customerName || (customerId ? `Client #${customerId}` : 'Client inconnu')
        const count = Number(salesCount || 0)
        const salesSuffix = count > 0 ? ` (${count} vente${count > 1 ? 's' : ''})` : ''
        return `Facture client creee: ${invoiceRef} pour ${customerLabel}${salesSuffix}`
    }

    buildLocalNotification({ type, message, data = {} }) {
        return {
            id: `local-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
            type,
            message,
            data,
            read_at: null,
            created_at: new Date().toISOString(),
            local_only: true
        }
    }

    // Récupérer les notifications de l'utilisateur
    getUserNotifications(params = {}) {
        const defaultParams = {
            page: params.page || 1,
            perPage: params.perPage || 15,
            unread: params.unread || false
        };

        return ApiService.get(`/notifications`, {
            params: defaultParams
        })
    }

    // Récupérer le nombre de notifications non lues
    getUnreadCount() {
        return ApiService.get(`/notifications/unread-count`)
    }

    // Marquer une notification comme lue
    markAsRead(notificationId) {
        return ApiService.post(`/notifications/mark-as-read/${notificationId}`)
    }

    // Marquer toutes les notifications comme lues
    markAllAsRead() {
        return ApiService.post(`/notifications/mark-all-as-read`)
    }

    // Supprimer une notification
    deleteNotification(notificationId) {
        return ApiService.delete(`/notifications/${notificationId}`)
    }

    // Envoyer une notification (nécessite des privilèges)
    sendNotification(data) {
        return ApiService.post(`/notifications/send`, data)
    }

    // Envoyer une notification à l'utilisateur connecté
    notifyCurrentUser({ type, message, data = {} }) {
        let user = null

        try {
            user = JSON.parse(localStorage.getItem('user') || 'null')
        } catch (error) {
            user = null
        }

        if (!user?.id) {
            return Promise.resolve(null)
        }

        return this.sendNotification({
            type,
            message,
            notifiable_type: 'App\\Models\\User',
            notifiable_id: user.id,
            data
        }).then(async (response) => {
            try {
                const notificationsStore = useNotificationsStore()
                const createdNotification = response?.data?.notification || response?.data?.data

                if (createdNotification) {
                    notificationsStore.addNotification({
                        ...createdNotification,
                        type: createdNotification?.type || type,
                        message: createdNotification?.message || message,
                        data: {
                            ...(data || {}),
                            ...(createdNotification?.data || {})
                        },
                        read_at: createdNotification?.read_at || null,
                        created_at: createdNotification?.created_at || new Date().toISOString(),
                        local_only: false
                    })

                    notificationsStore.loadRecentNotifications({ page: 1, perPage: 5, unread: true })
                    notificationsStore.loadNotificationCount()
                } else {
                    // Certaines reponses API ne renvoient pas l'objet notification cree;
                    // on injecte une notification immediate pour eviter un panneau vide.
                    notificationsStore.addNotification({
                        type,
                        message,
                        data,
                        read_at: null,
                        created_at: new Date().toISOString(),
                        local_only: true
                    })

                    await notificationsStore.loadNotificationCount()
                    await notificationsStore.loadRecentNotifications({ page: 1, perPage: 5, unread: true })
                }
            } catch (storeError) {
                console.warn('Impossible de synchroniser le panneau de notifications:', storeError)
            }

            return response
        }).catch((error) => {
            try {
                const notificationsStore = useNotificationsStore()
                const localNotification = this.buildLocalNotification({ type, message, data })
                notificationsStore.addNotification(localNotification)
            } catch (storeError) {
                console.warn('Impossible d\'injecter la notification locale:', storeError)
            }

            console.warn('Envoi API notification echoue, fallback local actif:', error)
            return null
        })
    }

    // Routes FCM (Push notifications)
    // POST /notifications/fcm/register
    // Body: { token, device_type, device_id, app_version }
    registerToken(data) {
        return ApiService.post(`/notifications/fcm/register`, data)
    }

    // POST /notifications/fcm/remove
    // Body: { token }
    removeToken(data) {
        return ApiService.post(`/notifications/fcm/remove`, data)
    }

    getTokens() {
        return ApiService.get(`/notifications/fcm/tokens`)
    }
}

export default new NoticationsService();